import { ExtraPropertyEntityType } from '@tribeplatform/gql-client/types'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import {
  useNetwork,
  useUpsertExtraProperty,
} from '@tribeplatform/react-sdk/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { sectionsFactory } from '../components/Admin/Onboarding/sections'
import { useAnalytics } from './useAnalytics'

export const SKIP_ADMIN_ONBOARDING_DATE = '2022-06-06'

export const useAdminOnboarding = () => {
  const sections = useStaticIntl(sectionsFactory)
  const { mutateAsync: upsertExtraProperty, isLoading } =
    useUpsertExtraProperty()
  const { data: network } = useNetwork()
  const analytics = useAnalytics()

  const autocompleteSteps = () => {
    let completedItems = ['create-community']
    if (new Date(network?.createdAt) < new Date(SKIP_ADMIN_ONBOARDING_DATE)) {
      completedItems = completedItems.concat([
        'create-space',
        'invite-teammates',
        'permission-and-privacy',
        'login-methods',
        'profile-fields',
        'look-and-feel',
        'invite-members',
        'moderation-rules',
      ])
    }
    if (network?.logo?.id || network?.favicon?.id)
      completedItems.push('upload-logo')
    if (!network?.domain.match(/\.tribeplatform\.com$/))
      completedItems.push('connect-domain')
    if (network?.memberFields?.fields?.length > 0)
      completedItems.push('profile-fields')

    upsertExtraProperty({
      input: {
        entityType: ExtraPropertyEntityType.Network,
        entityId: network?.id,
        key: 'internal:onboarding',
        value: JSON.stringify(completedItems),
      },
    })
  }

  const getTotalSteps = () => {
    return sections.reduce((stepsCount, section) => {
      return stepsCount + section?.children?.length
    }, 0)
  }

  const getCompletedSteps = () => {
    let completedItems = []
    try {
      completedItems = JSON.parse(
        network?.extraProperties?.find(
          property => property.key === 'internal:onboarding',
        )?.value || '[]',
      )
    } catch (err) {
      logger.error('Cannot parse appWishList JSON object')
    }
    return completedItems
  }

  const completeStep = ({ step }) => {
    let completedItems = []
    const totalSteps = getTotalSteps()

    if (step !== false) {
      completedItems = getCompletedSteps()
      if (completedItems.indexOf(step) !== -1) return
      completedItems.push(step)
    }

    upsertExtraProperty({
      input: {
        entityType: ExtraPropertyEntityType.Network,
        entityId: network?.id,
        key: 'internal:onboarding',
        value: JSON.stringify(completedItems),
      },
    })
    const remainingSteps = Math.max(0, totalSteps - completedItems.length)

    analytics.track('Complete Admin Onboarding', {
      step: step || 'reset-onboarding',
      total: totalSteps,
      index: completedItems.length,
      remaining: remainingSteps,
    })

    if (step !== false) {
      toast({
        title: 'Onboarding step completed!',
        description: `Amazing job! ${
          remainingSteps === 1
            ? `${remainingSteps} more step`
            : `${remainingSteps} more steps`
        } to go.`,
        icon: <span className="text-3xl">🎉</span>,
      })
    }
  }

  return {
    isLoading,
    getTotalSteps,
    completeStep,
    getCompletedSteps,
    autocompleteSteps,
  }
}
